import type { SharedContractView } from "@closer/shared";
import { Badge, Card, PageHeader, Skeleton } from "@closer/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";

import { StateView } from "../../components/StateView";
import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";

/**
 * PAYLAŞILAN SÖZLEŞMELER — katılımcı organizasyon tarafı (CLOS-873; CLOS-154 `[140]`).
 *
 * ### ⚠ Bu ekran ölçülmüş bir ÇIKMAZ SOKAĞI kapatıyor
 *
 * `/api/shared-contracts` mount'u tam ve testliydi; `apps/web` altında ise test dosyaları
 * dâhil SIFIR referansı vardı. Yani bir organizasyona sözleşme paylaşılıyor ama paylaşılan
 * taraf onu hiçbir ekrandan göremiyordu — paylaşım özelliği fiilen tek taraflıydı.
 *
 * ### ⚠ "Tüm Sözleşmeler"in süzgeci DEĞİL
 *
 * Kayıt SAHİBİN tenant'ında yaşıyor; burada gördüğümüz şey kopya değil, kanonik kimliğin
 * ta kendisi (`[140]` kabul kriteri: *"tenant bazlı belge kopyası ana kayıt gibi
 * davranmaz"*). Bu yüzden satırlar `/api/contracts` listesine karıştırılmıyor.
 *
 * ### ⚠ ERİŞİM DÜZEYİ bir YETKİ ETİKETİ DEĞİL
 *
 * `accessLevel` sahibin PAYLAŞIM BEYANI. Ölçüldü: katılım satırının `accessLevel` ve
 * `permissions` alanları paylaşılan yazma yollarında hiç okunmuyor. Fiilî yapabilirlik üç
 * ayrı eksenden geliyor — kendi alanındaki rol (`requirePermission`), sahip bağlamında
 * kayıt erişimi (`hasRecordAccess`), ve üzerinde çalışılacak malzemenin açık paylaşımı
 * (`sourceShareId`). Bu yüzden etiket "yetkiniz" demiyor; eylem düğmeleri de bu değerden
 * DEĞİL gerçek yapabilirlikten türüyor (bkz. `SharedNegotiationSection`).
 *
 * ⚠ ACIK-KARAR (CLOS-873): **`accessLevel` beyanı UYGULANMALI MI, yoksa bilgi olarak mı
 * kalmalı?** Bugün beyan ile fiilî durum ayrışabiliyor: sahip "Görüntüleme" paylaşımı
 * yapmışken karşı taraf, kendi rolü ve kendisine açılan malzeme sayesinde müzakereye
 * karar verebiliyor. Tutarlı bir tasarım olabilir (beyan bir niyet, kapılar ayrı) ama
 * güvenliğe komşu bir semantik ve varsayımla kapatılamaz: karta yazıldı, ürün sahibinin
 * kararı bekleniyor. Karar "uygulansın" çıkarsa değişecek yer BU EKRAN DEĞİL, sunucudaki
 * yazma yolları — ekranın düğmeyi gizlemesi bir kapı değil, kolaylıktır.
 */
export function SharedContractsPage() {
  const { activeTenantId } = useAuth();
  const tenantId = activeTenantId!;

  const sorgu = useQuery({
    queryKey: tenantKey(tenantId, "paylasilan-sozlesmeler"),
    enabled: activeTenantId !== null,
    queryFn: async () =>
      (await api<SharedContractView[]>("/api/shared-contracts", { tenantId })).data,
  });

  /*
    ⚠ Yetki kapısı İSTEMCİDE KURULMUYOR (CLOS-874'ün eski gelen-davetler ekranı emsali; o ekran CLOS-1128'de Davetlerim'e yönlendirildi). Sorgu
    koşulsuz gidiyor ve yetkisizliği SUNUCU söylüyor; ekran o cevabı sunuyor. `can` ile
    öndeneme yapsaydık yetki sorusuna iki ayrı cevap veren iki kapı doğardı ve biri gün
    gelip diğerinden gevşerdi (`requirePermission` yorumundaki gerekçe).
  */
  const hata = sorgu.error
    ? resolveErrorState(sorgu.error, {
        idempotent: true,
        existenceIsSecret: false,
        overrides: {
          forbidden: {
            title: "Paylaşılan sözleşmeleri görme yetkiniz yok",
            description: "Bu ekran sözleşme görüntüleme yetkisi gerektirir.",
          },
          error: {
            title: "Paylaşılan sözleşmeler yüklenemedi",
            description: "Bağlantı sorunları geçici olabilir.",
          },
        },
      })
    : null;

  const satirlar = sorgu.data ?? [];

  return (
    <section className="flex w-full flex-col gap-6">
      <PageHeader
        title="Paylaşılanlar"
        description="Başka organizasyonların çalışma alanınızla birlikte yürüttüğü sözleşmeler."
      />

      {sorgu.isPending ? (
        <div role="status" aria-label="Paylaşılan sözleşmeler yükleniyor">
          <Skeleton lines={3} />
        </div>
      ) : hata ? (
        <StateView state={hata} onRetry={() => void sorgu.refetch()} />
      ) : satirlar.length === 0 ? (
        <Card>
          <p data-testid="paylasim-yok" className="text-sm text-fg-muted">
            Sizinle paylaşılan bir sözleşme yok.
          </p>
        </Card>
      ) : (
        <>
          {/*
            ⚠ BEYAN ROZETİNİN YANINDA DEĞİL, LİSTENİN ÜSTÜNDE — bir kez.
            Rozet her satırda tekrar ediyor; açıklamayı da her satırda tekrarlamak
            listeyi okunamaz hâle getirirdi. Cümle listenin başında duruyor ve
            kullanıcı ilk rozete bakmadan önce onu okuyor.

            ⚠ NEDEN VAR: rozet doğru dili kullanıyordu ("paylaşım düzeyi", "yetkiniz"
            değil) ama testi yapan kişi onu kendi yetkisi sandı — aynı ekrandan
            müzakereye katılabildiği için ürünü tutarsız bildirdi. Ürün doğruydu:
            beyan sahibin niyeti, fiilî yapabilirlik kendi rolü + kendisine açılan
            malzeme + kayıt erişiminin KESİŞİMİ. Bu ayrım bugüne kadar yalnız bu
            dosyanın kod yorumunda yazıyordu; kullanıcı kod yorumu okumuyor.
          */}
          <p data-testid="paylasim-duzeyi-aciklama" className="text-sm text-fg-muted">
            Satırlardaki “Paylaşım düzeyi”, sözleşmeyi paylaşan tarafın beyanıdır. Bu sözleşmede
            neler yapabileceğinizi kendi rolünüz ve size açılan malzeme birlikte belirler.
          </p>
          <ul className="flex flex-col divide-y divide-border-subtle rounded-lg border border-border-default bg-surface">
            {satirlar.map((satir) => (
              <li key={satir.contractId} className="px-5 py-4 transition-colors hover:bg-muted">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex min-w-0 flex-col gap-1">
                    {/*
                      ⚠ Başlık BAĞLANTI: kartın kapattığı kusur "görülemiyor" değil
                      "ulaşılamıyor"du. Liste satırı detaya girmiyorsa ekran boşluğu
                      yalnız yer değiştirmiş olurdu.
                    */}
                    <Link
                      to={`/app/sozlesmeler/paylasilanlar/${satir.contractId}`}
                      className="text-base font-medium text-fg-strong underline-offset-2 hover:underline"
                    >
                      {satir.title}
                    </Link>
                    <span className="text-sm text-fg-muted">{satir.ownerName} paylaştı</span>
                  </div>
                  {/*
                    ⚠ "Paylaşım düzeyi" — "yetkiniz" DEĞİL. Bu sahibin beyanı; ne
                    yapabildiğinizi kendi rolünüz ve size açılan malzeme belirliyor.
                  */}
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge tone={satir.accessLevel === "write" ? "warning" : "neutral"}>
                      Paylaşım düzeyi: {satir.accessLevel === "write" ? "Düzenleme" : "Görüntüleme"}
                    </Badge>
                    {/*
                      ⚠ SÜRE GÖRÜNÜR (CLOS-147 `[128]`): erişimin bittiği an kullanıcıdan
                      saklanırsa, bir gün sayfa kapandığında sebebini hiçbir yerde okuyamaz
                      ve karşı tarafa "sistem bozuk" diye döner.
                      ⚠ Süresiz erişimde ROZET HİÇ ÇİZİLMİYOR: "bitiş yok" ile "bitiş
                      bilinmiyor" aynı şey değil ve boş bir tarih ikisini karıştırırdı.
                    */}
                    {satir.accessEndsAt ? (
                      <Badge tone="neutral">
                        Erişim bitişi: {new Date(satir.accessEndsAt).toLocaleString("tr-TR")}
                      </Badge>
                    ) : null}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
